import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCurrentProject } from '../../lib/use-project.js';
import { useUrlState } from '../../lib/use-url-state.js';
import { E2eTab } from './e2e-tab.js';
import { GlossaryTab } from './glossary-tab.js';
import { ProcessPanel } from './process-panel.js';
import { ProcessTree } from './process-tree.js';
import { SimplificationsTab } from './simplifications-tab.js';
import { TestCaseDetail } from './testcase-detail.js';
import { TestCasePanel } from './testcase-panel.js';
import { TestDataTab } from './testdata-tab.js';
import type { Selection } from './types.js';

// Design-Spec 3.5 / Screen 3a: Katalog-Seite mit Kontextzeile (Tabs + Suche) und
// Tab-Panel darunter. Das Projekt kommt NICHT aus der URL, sondern aus `useCurrentProject`
// (V1: genau ein Projekt). `projectId={project.project_id}` wird an jedes Tab-Panel durchgereicht.

type CatalogTab = 'processes' | 'e2e' | 'testdata' | 'glossary' | 'simplifications';

const TABS: CatalogTab[] = ['processes', 'e2e', 'testdata', 'glossary', 'simplifications'];

// #28: Die rechte Spalte zeigt je nach Baum-Auswahl Testfall-Detail, Testfall-Anlage, die
// Prozess-Maske (anlegen/bearbeiten) oder gar nichts. Die Dirty-Regel ist dieselbe wie bei
// CatalogTable-Zeilen: ein Panel mit ungespeicherten Eingaben gibt den Platz nicht frei
// (Spec Kapitel 3).
export function ProcessesPanel({ projectId, q }: { projectId: string; q?: string }) {
  const { t } = useTranslation();
  // #42: der ausgewählte Testfall lebt als `?test_case=<id>` in der URL — deep-linkbar und
  // reload-fest. Nur die Testfall-Auswahl geht in die URL; die anderen Auswahlarten (Prozess,
  // Neuanlage) entfernen den Param wieder, statt ihn stehenzulassen. Der lokale `selection`-State
  // bleibt die Render-Quelle, damit die bestehende Dirty-Guard-Logik unverändert greift; die URL
  // ist nur eine Persistenz-Schicht darüber. Vorwahl beim Laden über die Lazy-Init aus dem Param.
  const [testCaseParam, setTestCaseParam] = useUrlState('test_case', '');
  const [selection, setSelection] = useState<Selection>(
    testCaseParam ? { kind: 'testcase', testCaseId: testCaseParam } : null,
  );
  const [panelDirty, setPanelDirty] = useState(false);

  // Auswahl setzen UND in der URL spiegeln. Ohne Dirty-Guard — direkt aufgerufen an den Stellen,
  // die die Auswahl bewusst OHNE Rücksicht auf panelDirty ändern (Anlage abgeschlossen/abgebrochen,
  // Prozess gespeichert/gelöscht), analog zum bisherigen direkten `setSelection`.
  const applySelection = (next: Selection) => {
    setSelection(next);
    setTestCaseParam(next?.kind === 'testcase' ? next.testCaseId : '');
  };

  const select = (next: Selection) => {
    if (panelDirty) return;
    applySelection(next);
  };

  return (
    <div className="flex flex-1 overflow-hidden">
      <ProcessTree projectId={projectId} selection={selection} onSelect={select} q={q} />
      <div className="flex-1 overflow-y-auto">
        {selection === null && (
          <div className="p-6 text-text-muted">{t('catalog.detail.selectHint')}</div>
        )}
        {selection?.kind === 'testcase' && (
          <TestCaseDetail
            projectId={projectId}
            testCaseId={selection.testCaseId}
            onDeleted={() => applySelection(null)}
          />
        )}
        {selection?.kind === 'testcase-new' && (
          <TestCasePanel
            // Identitaet statt Objekt: ein Wechsel des Prozesses baut das Panel neu auf, statt
            // den Entwurf des vorigen durchbluten zu lassen (Lehre aus #37).
            key={selection.processId}
            projectId={projectId}
            processId={selection.processId}
            // Bewusst setSelection statt select: TestCasePanel ruft onDirtyChange(false) synchron
            // VOR onCreated auf, aber panelDirty ist in diesem Zyklus noch der alte (veraltete)
            // Closure-Wert (true). select(...) wuerde daher den eigenen Sprung auf den neu
            // angelegten Testfall blockieren (siehe testcase-panel.tsx).
            onCreated={(testCaseId) => applySelection({ kind: 'testcase', testCaseId })}
            onCancel={() => applySelection(null)}
            onDirtyChange={setPanelDirty}
          />
        )}
        {(selection?.kind === 'process' || selection?.kind === 'process-new') && (
          <ProcessPanel
            key={selection.kind === 'process' ? selection.processId : 'neu'}
            projectId={projectId}
            processId={selection.kind === 'process' ? selection.processId : null}
            onSaved={(processId) => applySelection({ kind: 'process', processId })}
            onDeleted={() => applySelection(null)}
            onCancel={() => applySelection(null)}
            onDirtyChange={setPanelDirty}
          />
        )}
      </div>
    </div>
  );
}

function CatalogTabPanel({
  tab,
  projectId,
  q,
}: {
  tab: CatalogTab;
  projectId: string;
  // #78: der Suchbegriff filtert IMMER nur den aktiven Tab (Task-Brief-Vorgabe) — deshalb reicht
  // ein einzelner Wert, der an genau ein Tab-Panel weitergereicht wird.
  q: string;
}) {
  switch (tab) {
    case 'processes':
      return <ProcessesPanel projectId={projectId} q={q} />;
    case 'e2e':
      return <E2eTab projectId={projectId} q={q} />;
    case 'testdata':
      return <TestDataTab projectId={projectId} q={q} />;
    case 'glossary':
      return <GlossaryTab projectId={projectId} q={q} />;
    case 'simplifications':
      return <SimplificationsTab projectId={projectId} q={q} />;
    default: {
      const _exhaustive: never = tab;
      void _exhaustive;
      void projectId;
      void q;
      return null;
    }
  }
}

export function CatalogPage() {
  const { t } = useTranslation();
  const { project, isLoading } = useCurrentProject();
  // #36: der aktive Tab lebt in der URL (deep-linkbar, reload-fest). Auf die gültigen Tabs
  // normalisiert, damit ein unbekanntes `?tab=...` auf die Prozess-Vorgabe zurückfällt.
  const [tabParam, setActiveTab] = useUrlState('tab', 'processes');
  const activeTab: CatalogTab = (TABS as string[]).includes(tabParam)
    ? (tabParam as CatalogTab)
    : 'processes';
  // #78: der Suchbegriff lebt ebenfalls in der URL — dasselbe use-url-state.ts-Muster wie beim
  // aktiven Tab (#36/#42). Das Feld zeigt den rohen (ungetrimmten) Wert, damit z. B. ein
  // Leerzeichen beim Tippen nicht sofort wieder verschwindet — gefiltert wird mit dem getrimmten
  // Begriff (Task-Brief: `toLowerCase().includes(...)` auf dem getrimmten Begriff).
  const [qParam, setQParam] = useUrlState('q', '');
  const query = qParam.trim();

  if (isLoading) {
    return <div className="p-6 text-text-muted">{t('catalog.loading')}</div>;
  }

  if (!project) {
    return <div className="p-6 text-text-muted">{t('catalog.noProject')}</div>;
  }

  return (
    <div className="flex h-[calc(100vh-58px)] flex-col">
      <div className="flex items-center justify-between gap-4 border-b border-[var(--border)] px-6 py-3">
        <nav className="flex items-center gap-4 text-sm">
          {TABS.map((tab) => (
            <button
              key={tab}
              type="button"
              onClick={() => setActiveTab(tab)}
              className={`border-b-2 pb-1 ${
                activeTab === tab
                  ? 'border-brand-accent text-brand-accent'
                  : 'border-transparent text-text-muted hover:text-text-body'
              }`}
            >
              {t(`catalog.tabs.${tab}`)}
            </button>
          ))}
        </nav>
        <input
          type="search"
          value={qParam}
          onChange={(e) => setQParam(e.target.value)}
          placeholder={t('catalog.searchPlaceholder')}
          aria-label={t('catalog.search.ariaLabel')}
          className="w-64 rounded-btn border border-[var(--border)] bg-bg-surface px-3 py-1.5 text-sm text-text-body"
        />
      </div>
      <CatalogTabPanel tab={activeTab} projectId={project.project_id} q={query} />
    </div>
  );
}
